/**
 * แดชบอร์ด HR — "ศูนย์งาน HR วันนี้".
 *
 * KPI strip (ภาพรวมเร็ว) + queue view ของงานที่ต้องลงมือจริง 3 คิว:
 *   1) รออนุมัติ (ลา/OT)  2) ปัญหาเวลาเข้างาน (escalation)  3) ประวัติพนักงานไม่ครบ
 *   4) ครบทดลองงาน (โผล่เฉพาะตอนมีคนถึงคิว)
 * แต่ละคิวโชว์รายการจริงสองสามอันแรก + ลิงก์ไปจัดการต่อ แทนที่จะโชว์แค่ตัวเลข.
 */
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { StatCard } from '../../components/ui/StatCard';
import {
  usePendingApprovals, usePendingChanges, useEmployees, useEscalations, useFollowupList, useProbationList,
} from '../../hooks';
import {
  Users, ListChecks, ClipboardCheck, AlertTriangle, FileText, Clock, UserCircle, ChevronRight, CheckCircle2, CalendarClock,
} from 'lucide-react';

const ESC_TYPE_LABEL: Record<string, string> = {
  short_work: 'ทำงานไม่ครบเวลา',
  late: 'เข้างานสาย',
  absent_no_evidence: 'ขาดงาน (ไม่มีหลักฐาน)',
  over_leave_quota: 'ลาเกินสิทธิ์',
  checkin_geofence_failed: 'เช็คอินนอกพื้นที่',
  conditional_evidence_overdue: 'เลยกำหนดส่งหลักฐาน',
  profile_incomplete: 'ประวัติไม่ครบ',
  missing_break_scan: 'ไม่สแกนพักเที่ยง',
  missing_clock_out: 'ไม่สแกนออกงาน',
  emergency_leave_review: 'ลาฉุกเฉินรอตัดสิน',
  no_return_followup: 'ไม่กลับมาทำงาน',
  ot_mismatch: 'OT ไม่ตรงเวลาจริง',
  ot_unrequested: 'OT ไม่ได้ขออนุมัติ',
  unscheduled_work: 'มาทำงานวันที่ไม่ได้กำหนด',
  time_shortfall: 'เข้างานไม่ครบเวลา (หักค่าจ้าง)',
};

/** escalation แต่ละชนิดไปจัดการที่ไหน */
function escRoute(type: string): string {
  if (type === 'emergency_leave_review') return '/approvals#emergency';
  if (type === 'profile_incomplete') return '/hr/followup';
  return '/owner/cycle#reconcile';
}

interface PendingLeaveLite { leave_id: string; emp_code: string; emp_name?: string | null; leave_type: string; date: string }
interface PendingOTLite { ot_id?: string; emp_code: string; emp_name?: string | null; date: string; hours?: number }
interface EscalationLite { id: string; emp_code: string; type: string; date: string; status: string }
interface FollowupLite { emp_code: string; emp_name: string; profile_completion_pct: number; followup_status: string }

const LEAVE_TYPE_LABEL: Record<string, string> = {
  sick: 'ลาป่วย', personal: 'ลากิจ', vacation: 'ลาพักร้อน', maternity: 'ลาคลอด', ordination: 'ลาบวช', other: 'ลาอื่นๆ',
};

function QueueCard({
  icon: Icon, title, count, tone, to, toLabel, empty, children,
}: {
  icon: typeof FileText; title: string; count: number;
  tone: 'warn' | 'danger' | 'neutral'; to: string; toLabel: string;
  empty: string; children: React.ReactNode;
}) {
  const badge =
    count === 0 ? 'bg-brand-100 text-brand-700'
      : tone === 'danger' ? 'bg-red-100 text-red-700'
        : 'bg-amber-100 text-amber-700';
  return (
    <section className="card overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
        <Icon className="w-4 h-4 text-gray-400" />
        <h2 className="font-semibold text-sm flex-1">{title}</h2>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge}`}>{count} รายการ</span>
      </div>
      {count === 0 ? (
        <div className="px-4 py-5 text-sm text-gray-400 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-brand-400" /> {empty}
        </div>
      ) : (
        <>
          <div className="divide-y divide-gray-50">{children}</div>
          <Link to={to} className="flex items-center justify-center gap-1 px-4 py-2.5 text-sm font-medium text-brand-700 hover:bg-gray-50 border-t border-gray-100">
            {toLabel} <ChevronRight className="w-4 h-4" />
          </Link>
        </>
      )}
    </section>
  );
}

function QueueRow({ to, name, sub, meta }: { to: string; name: string; sub: string; meta?: string }) {
  return (
    <Link to={to} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50">
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate">{name}</div>
        <div className="text-xs text-gray-500 truncate">{sub}</div>
      </div>
      {meta && <span className="text-xs text-gray-400 shrink-0">{meta}</span>}
      <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
    </Link>
  );
}

export function HRDashboard() {
  const { data: approvals } = usePendingApprovals() as { data: { leaves: PendingLeaveLite[]; ots: PendingOTLite[] } | undefined };
  const { data: pendingChanges } = usePendingChanges() as { data: Array<{ status: string }> | undefined };
  const { data: employees } = useEmployees() as { data: unknown[] | undefined };
  const { data: escalations } = useEscalations() as { data: EscalationLite[] | undefined };
  const { data: followup } = useFollowupList('active') as { data: { rows: FollowupLite[] } | undefined };
  const { data: probation } = useProbationList('queue');

  const leaves = approvals?.leaves ?? [];
  const ots = approvals?.ots ?? [];
  const leaveCount = leaves.length;
  const otCount = ots.length;
  const pendingApprovalsCount = leaveCount + otCount;
  const pendingChangesCount = (pendingChanges ?? []).filter((c) => c.status === 'pending').length;

  const attendanceIssues = (escalations ?? []).filter((e) => e.status === 'pending');
  const profileGaps = (followup?.rows ?? []).filter((r) => r.profile_completion_pct < 100);

  const employeeCount = employees?.length ?? 0;
  const probationRows = probation?.rows ?? [];
  const probationDue = probationRows.filter((r) => r.days_left <= 0).length;

  // คิวรออนุมัติ — รวมลา + OT เรียงพอประมาณ (โชว์ 4 แรก)
  const approvalRows = [
    ...leaves.map((l) => ({
      key: `l-${l.leave_id}`,
      name: l.emp_name ?? l.emp_code,
      sub: `${LEAVE_TYPE_LABEL[l.leave_type] ?? l.leave_type} · ${l.date}`,
    })),
    ...ots.map((o) => ({
      key: `o-${o.ot_id ?? o.emp_code}-${o.date}`,
      name: o.emp_name ?? o.emp_code,
      sub: `OT${o.hours ? ` ${o.hours} ชม.` : ''} · ${o.date}`,
    })),
  ].slice(0, 4);

  return (
    <AdminLayout title="แดชบอร์ด" subtitle="ศูนย์งาน HR วันนี้">
      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <StatCard icon={Users} label="พนักงานทั้งหมด" value={employeeCount} to="/hr/employees" />
        <StatCard
          icon={ListChecks}
          label="รออนุมัติ"
          value={pendingApprovalsCount}
          hint={`ลา ${leaveCount} · OT ${otCount}`}
          tone={pendingApprovalsCount > 0 ? 'warn' : 'success'}
          to="/approvals"
        />
        <StatCard
          icon={AlertTriangle}
          label="ปัญหาเวลาเข้างาน"
          value={attendanceIssues.length}
          tone={attendanceIssues.length > 0 ? 'danger' : 'success'}
        />
        <StatCard
          icon={UserCircle}
          label="ประวัติไม่ครบ"
          value={profileGaps.length}
          tone={profileGaps.length > 0 ? 'warn' : 'success'}
          to="/hr/followup"
        />
        <StatCard
          icon={CalendarClock}
          label="ครบทดลองงาน"
          value={probationRows.length}
          hint={probationDue > 0 ? `ถึงกำหนดแล้ว ${probationDue} คน` : undefined}
          tone={probationDue > 0 ? 'danger' : probationRows.length > 0 ? 'warn' : 'success'}
          to="/hr/people#probation"
        />
      </div>

      {/* Queue view */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <QueueCard
          icon={FileText}
          title="รออนุมัติ (ลา / OT)"
          count={pendingApprovalsCount}
          tone="warn"
          to="/approvals"
          toLabel="เปิดหน้าอนุมัติทั้งหมด"
          empty="ไม่มีคำขอค้างอนุมัติ"
        >
          {approvalRows.map((r) => (
            <QueueRow key={r.key} to="/approvals" name={r.name} sub={r.sub} />
          ))}
        </QueueCard>

        <QueueCard
          icon={Clock}
          title="ปัญหาเวลาเข้างาน"
          count={attendanceIssues.length}
          tone="danger"
          to="/owner/cycle#reconcile"
          toLabel="ดูเรื่องค้างทั้งหมด"
          empty="ไม่มีเรื่องเวลาเข้างานค้าง"
        >
          {attendanceIssues.slice(0, 4).map((e) => (
            <QueueRow
              key={e.id}
              to={escRoute(e.type)}
              name={ESC_TYPE_LABEL[e.type] ?? e.type}
              sub={e.emp_code}
              meta={e.date}
            />
          ))}
        </QueueCard>

        <QueueCard
          icon={UserCircle}
          title="ประวัติพนักงานไม่ครบ"
          count={profileGaps.length}
          tone="warn"
          to="/hr/followup"
          toLabel="ตามเก็บประวัติทั้งหมด"
          empty="พนักงานกรอกประวัติครบทุกคน"
        >
          {profileGaps.slice(0, 4).map((r) => (
            <QueueRow
              key={r.emp_code}
              to="/hr/followup"
              name={r.emp_name || r.emp_code}
              sub={`กรอกแล้ว ${r.profile_completion_pct}%`}
              meta={r.followup_status === 'escalated' ? 'escalated' : undefined}
            />
          ))}
        </QueueCard>

        {probationRows.length > 0 && (
          <QueueCard
            icon={CalendarClock}
            title="ครบทดลองงาน — รอตัดสิน"
            count={probationRows.length}
            tone={probationDue > 0 ? 'danger' : 'warn'}
            to="/hr/people#probation"
            toLabel="ไปตัดสินผ่าน/ต่อโปร"
            empty="ยังไม่มีใครถึงกำหนด"
          >
            {probationRows.slice(0, 4).map((r) => (
              <QueueRow
                key={r.emp_code}
                to="/hr/people#probation"
                name={r.emp_name}
                sub={`ครบ ${r.probation_end_date}`}
                meta={r.days_left < 0 ? `เลย ${Math.abs(r.days_left)} วัน` : r.days_left === 0 ? 'วันนี้' : `อีก ${r.days_left} วัน`}
              />
            ))}
          </QueueCard>
        )}
      </div>

      {/* คำขอแก้ไขที่รอ Owner ตัดสิน (ยังคงไว้) */}
      {pendingChangesCount > 0 && (
        <Link to="/approvals#changes" className="card flex items-center gap-3 px-4 py-3 mt-4 hover:bg-gray-50">
          <ClipboardCheck className="w-4 h-4 text-gray-400" />
          <span className="text-sm flex-1">คำขอแก้ไขข้อมูลที่รอ Owner อนุมัติ</span>
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">{pendingChangesCount} รายการ</span>
          <ChevronRight className="w-4 h-4 text-gray-300" />
        </Link>
      )}
    </AdminLayout>
  );
}
